import { createFileRoute } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import { CtaBanner, FaqAccordion, InvestShowcase } from '~/components/sections';
import { ButtonLink, Container, Grid, SmartImage } from '~/components/ui';
import {
  investBelief,
  investDisclaimer,
  investFaq,
  investHero,
  investStart,
} from '~/content/invest';
import { trustedBySection } from '~/content/shared';
import { pageHeadMeta } from '~/lib/seo';
import investStylesHref from '~/styles/invest.css?url';

/** Small document/article glyph (inherits `currentColor`, so it renders white here). */
function ArticleIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}

export const Route = createFileRoute('/invest')({
  head: () =>
    pageHeadMeta({
      title: 'Invest in US Stocks, ETFs, Bonds and Gold indices',
      description:
        'Invest in U.S. stocks, ETFs, bonds, and gold indices with nsave. Build your portfolio and trade globally and safely today.',
      path: '/invest',
      stylesheets: [investStylesHref],
    }),
  component: InvestPage,
});

/**
 * Invest hero — a static, page-specific hero (deliberately NOT the shared
 * `Hero`, which animates the image, crops it full-bleed and shows the stats
 * row). Centred heading + "Get the app" CTA, then the hero image rendered
 * fully visible at its natural aspect ratio across the full 9-column width.
 */
function InvestHero() {
  return (
    <Section className="pt-16 md:pt-24 !pb-0">
      <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
        <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] text-balance whitespace-pre-line md:text-title-m">
          {investHero.title}
        </h1>
        <ButtonLink href={investHero.cta.href} size="md" className="mt-9 md:mt-14">
          {investHero.cta.label}
        </ButtonLink>
      </div>

      <Grid className="mt-[108px] md:mt-32">
        {/* On narrow viewports the image keeps a min width and overflows the
            column, so the phones stay large and the sides crop symmetrically
            (flex-centred). On desktop it fills the column and stops growing at
            the container width, staying fully visible. */}
        <div className="col-span-9 flex justify-center overflow-hidden">
          <SmartImage
            image={investHero.image}
            loading="eager"
            className="invest-hero-image h-auto w-full max-w-none min-w-[640px] shrink-0"
          />
        </div>
      </Grid>
    </Section>
  );
}

/**
 * Centered belief statement + reused press/publications logo row (same asset and
 * styling as the shared "Trusted by" section). Sits between the hero and the
 * stocks showcase.
 */
function InvestBelief() {
  const { publications } = trustedBySection;
  return (
    <Section className="-mt-10">
      <Grid>
        <div className="col-span-9 mx-auto flex max-w-3xl flex-col items-center text-center lg:col-span-7 lg:col-start-2">
          <div className="flex flex-col gap-6">
            {investBelief.lines.map((line) => (
              <p
                key={line}
                className="text-h4 font-semibold tracking-[-0.02em] text-balance text-ink md:text-h3">
                {line}
              </p>
            ))}
          </div>
          {publications && (
            <img
              src={publications.src}
              alt={publications.alt}
              className="mt-12 w-full max-w-[400px] object-contain"
            />
          )}
        </div>
      </Grid>
    </Section>
  );
}

/**
 * Full-bleed lifestyle band with left-overlaid white copy + article links. The
 * photo is edge-to-edge (breaks out of the container); the text sits in a
 * container/grid column so it starts at the same left edge as the rest of the
 * page. A left→right dark gradient keeps the white text legible over the photo.
 */
function InvestStart() {
  return (
    <section className="relative mt-20 w-full overflow-hidden">
      <img
        src={investStart.image.src}
        alt={investStart.image.alt}
        className="absolute inset-0 size-full object-cover object-[70%_center]"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/10" />
      <Container className="relative">
        <div className="flex min-h-[560px] items-center py-24 lg:min-h-[680px]">
          <Grid className="w-full">
            <div className="col-span-9 lg:col-span-5">
              <h2 className="text-h4 font-semibold text-balance text-white">{investStart.title}</h2>
              <p className="mt-6 max-w-md text-body text-white/90">{investStart.body}</p>
              <div className="mt-8 flex flex-col gap-4">
                {investStart.articles.map((article) => (
                  <a
                    key={article.label}
                    href={article.href}
                    className="inline-flex items-center gap-2 text-body font-medium text-white underline underline-offset-4 hover:text-white/80">
                    <ArticleIcon />
                    {article.label}
                  </a>
                ))}
              </div>
            </div>
          </Grid>
        </div>
      </Container>
    </section>
  );
}

/** Legal disclaimer, rendered directly below the FAQ. */
function InvestDisclaimer() {
  return (
    <Section className="!pt-0">
      <Grid>
        <div className="col-span-9 lg:col-span-7 lg:col-start-2">
          <p className="text-[13px] leading-[1.6] text-grey-500">{investDisclaimer}</p>
        </div>
      </Grid>
    </Section>
  );
}

function InvestPage() {
  return (
    <PageShell>
      <InvestHero />
      <InvestBelief />
      <InvestStart />
      <InvestShowcase />
      <FaqAccordion content={investFaq} />
      <InvestDisclaimer />
      <CtaBanner />
    </PageShell>
  );
}
